import { PostHog, PostHogProvider, usePostHog } from "posthog-js/react";
import { Fragment, useEffect, useMemo } from "react";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import config from "@vendor-app/config";

interface PostHogControlProps {
  enableCapture?: boolean;
}

const enablePostHogCapturing = (posthog: PostHog) => {
  posthog.opt_in_capturing();
  posthog.capture("$pageview");
  posthog.startSessionRecording();
};

const disablePostHogCapturing = (posthog: PostHog) => {
  posthog.opt_out_capturing();
  posthog.capture("$pageleave");
  posthog.stopSessionRecording();
};

/**
 * We only want posthog to capture on specific pages.
 * Use this hook to enable capture on the page.
 * You can use the `enableCapture` parameter to only enable
 * in certain scenarios.
 */
export const usePostHogControl = ({ enableCapture }: PostHogControlProps) => {
  const { posthogSessionReplay } = useSmartrrFlags();
  const posthog = usePostHog();

  const disableCapture = useMemo(
    () => !posthog || !enableCapture || !posthogSessionReplay,
    [posthog, enableCapture, posthogSessionReplay]
  );

  useEffect(() => {
    if (disableCapture) {
      return;
    }
    enablePostHogCapturing(posthog);

    // On component unmount we disable capture.
    return () => {
      disablePostHogCapturing(posthog);
    };
  }, [posthog, enableCapture, posthogSessionReplay]);
};

export const PostHogProviderWrapper = ({ children }) => {
  const { posthogSessionReplay } = useSmartrrFlags();

  if (!posthogSessionReplay) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <PostHogProvider
      apiKey={config.posthog.clientId}
      options={{
        api_host: config.posthog.publicPosthogHost,
        autocapture: false,
        capture_pageview: false,
      }}
    >
      {children}
    </PostHogProvider>
  );
};
